import { Component, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BonusqrService } from 'src/app/shared/servicio/bonusqr/bonusqr.service';
import { HelpersService } from 'src/app/shared/servicio/helpers/helpers.service';
import { ReportService } from 'src/app/admin/services/report.service';
import { AdminService } from 'src/app/admin/services/admin/admin.service';

interface MedicOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-bonus-qr-list',
  templateUrl: './bonus-qr-list.component.html',
  styleUrls: ['./bonus-qr-list.component.css'],
})
export class BonusQrListComponent implements OnInit {
  qrList: any[] = [];
  loading = false;

  filterActive: string = '';
  filterMedicId: number = null;
  filterMedicSearch = '';
  filterMedicResults: MedicOption[] = [];
  filterMedicLoading = false;
  showFilterMedicDropdown = false;
  private filterMedicSearch$ = new Subject<string>();

  page = 1;
  itemsPerPage = 10;

  createForm: FormGroup;
  creating = false;
  createSubmitted = false;
  modalRef: BsModalRef;

  createMedicSearch = '';
  createMedicResults: MedicOption[] = [];
  createMedicLoading = false;
  showCreateMedicDropdown = false;
  private comercialMedicsAll: MedicOption[] = [];
  private createMedicSearch$ = new Subject<string>();

  isAdmin = localStorage.getItem('type') === '3';
  assistants: { id: number; label: string }[] = [];

  constructor(
    private _bonusqr: BonusqrService,
    private _fb: FormBuilder,
    private _modalService: BsModalService,
    private _report: ReportService,
    private _admin: AdminService,
    public _helper: HelpersService
  ) {
    this.createForm = this._fb.group({
      medic_id: [null, Validators.required],
      assistant_id: [null],
      label: [''],
    });

    this.createForm.get('medic_id').valueChanges.subscribe((medicId) => this.loadAssistants(medicId));

    // Busca en el servidor solo cuando el usuario deja de escribir 300ms, y
    // descarta automáticamente respuestas de búsquedas anteriores (switchMap)
    // para que una respuesta lenta no sobrescriba con resultados desactualizados.
    this.createMedicSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => this.searchMedicsRemote(query, (v) => (this.createMedicLoading = v)))
      )
      .subscribe((options: MedicOption[]) => (this.createMedicResults = options));

    this.filterMedicSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => this.searchMedicsRemote(query, (v) => (this.filterMedicLoading = v)))
      )
      .subscribe((options: MedicOption[]) => (this.filterMedicResults = options));
  }

  // Busca médicos en el servidor (solo admin, que maneja miles y no se pueden precargar).
  // Se comparte entre el buscador del filtro y el del modal de creación.
  private searchMedicsRemote(query: string, setLoading: (v: boolean) => void) {
    if (query.length < 2) {
      return of([]);
    }
    setLoading(true);
    return this._report.getMedics({ paginate: false, search: query }).pipe(
      map((data: any) => (data.results || data || []).map((m: any) => ({ id: m.id, name: m.name }))),
      catchError(() => {
        this._helper.modalError('No se pudo buscar médicos');
        return of([]);
      }),
      map((options: MedicOption[]) => {
        setLoading(false);
        return options;
      })
    );
  }

  ngOnInit(): void {
    this.load();
    if (!this.isAdmin) {
      // Comercial ya maneja pocos médicos (los suyos): se precargan una sola vez
      // y el typeahead filtra en el navegador, sin ida y vuelta al servidor.
      this.loadComercialMedics();
    }
  }

  load(): void {
    this.loading = true;

    const filters: any = {};
    if (this.filterActive !== '') {
      filters.is_active = this.filterActive === 'true';
    }
    if (this.filterMedicId) {
      filters.medic_id = this.filterMedicId;
    }

    this._bonusqr.listBonusQr(filters).subscribe(
      (data: any) => {
        this.qrList = data.results || [];
        this.loading = false;
      },
      () => {
        this.loading = false;
        this._helper.modalError('No se pudo cargar el listado de QR');
      }
    );
  }

  private loadComercialMedics(): void {
    this._report.getMedicComercial().subscribe(
      (data: any) => {
        const options = (data || []).map((m: any) => ({
          id: m.id,
          name: `${m.user_id.first_name} ${m.user_id.last_name}`,
        }));
        this.filterMedicResults = options;
        this.comercialMedicsAll = options;
      },
      () => this._helper.modalError('No se pudo cargar el listado de médicos')
    );
  }

  onFilterMedicInput(term: string): void {
    this.filterMedicSearch = term;
    this.filterMedicId = null;
    this.showFilterMedicDropdown = true;

    if (this.isAdmin) {
      this.filterMedicSearch$.next(term.trim());
      return;
    }

    const query = term.trim().toLowerCase();
    this.filterMedicResults = !query
      ? this.comercialMedicsAll
      : this.comercialMedicsAll.filter((m) => m.name.toLowerCase().includes(query));
  }

  onFilterMedicFocus(): void {
    // Si ya hay un médico seleccionado, no reabrir la búsqueda solo por
    // hacer foco de nuevo (evita perder la selección sin querer).
    if (this.filterMedicId) {
      return;
    }
    this.showFilterMedicDropdown = true;
    if (this.isAdmin) {
      if (this.filterMedicSearch.trim().length >= 2) {
        this.filterMedicSearch$.next(this.filterMedicSearch.trim());
      }
      return;
    }
    const query = this.filterMedicSearch.trim().toLowerCase();
    this.filterMedicResults = !query
      ? this.comercialMedicsAll
      : this.comercialMedicsAll.filter((m) => m.name.toLowerCase().includes(query));
  }

  onFilterMedicBlur(): void {
    // Se retrasa el cierre para que el click sobre un ítem de la lista alcance a registrarse.
    setTimeout(() => { this.showFilterMedicDropdown = false; }, 150);
  }

  selectFilterMedic(match: MedicOption): void {
    this.filterMedicId = match.id;
    this.filterMedicSearch = match.name;
    this.showFilterMedicDropdown = false;
    this.load();
  }

  clearFilterMedic(): void {
    this.filterMedicId = null;
    this.filterMedicSearch = '';
    this.load();
  }

  onCreateMedicInput(term: string): void {
    this.createMedicSearch = term;
    this.createForm.get('medic_id').setValue(null);
    this.showCreateMedicDropdown = true;

    if (this.isAdmin) {
      this.createMedicSearch$.next(term.trim());
      return;
    }

    const query = term.trim().toLowerCase();
    this.createMedicResults = !query
      ? this.comercialMedicsAll
      : this.comercialMedicsAll.filter((m) => m.name.toLowerCase().includes(query));
  }

  onCreateMedicFocus(): void {
    // Si ya hay un médico seleccionado, no reabrir la búsqueda solo por
    // hacer foco de nuevo (evita perder la selección sin querer).
    if (this.createForm.get('medic_id').value) {
      return;
    }
    this.showCreateMedicDropdown = true;
    if (this.isAdmin) {
      if (this.createMedicSearch.trim().length >= 2) {
        this.createMedicSearch$.next(this.createMedicSearch.trim());
      }
      return;
    }
    const query = this.createMedicSearch.trim().toLowerCase();
    this.createMedicResults = !query
      ? this.comercialMedicsAll
      : this.comercialMedicsAll.filter((m) => m.name.toLowerCase().includes(query));
  }

  onCreateMedicBlur(): void {
    // Se retrasa el cierre para que el click sobre un ítem de la lista alcance a registrarse.
    setTimeout(() => { this.showCreateMedicDropdown = false; }, 150);
  }

  selectCreateMedic(match: MedicOption): void {
    this.createForm.get('medic_id').setValue(match.id);
    this.createMedicSearch = match.name;
    this.showCreateMedicDropdown = false;
  }

  loadAssistants(medicId: number): void {
    this.assistants = [];
    if (!this.isAdmin || !medicId) {
      return;
    }
    this._admin.Asistentes(medicId, null, null).subscribe(
      (data: any) => {
        this.assistants = (data.results || []).map((a: any) => ({
          id: a.id,
          label: `${a.user.first_name} ${a.user.last_name}`,
        }));
      },
      () => this._helper.modalError('No se pudo cargar el listado de asistentes')
    );
  }

  qrPrintUrl(code: string): string {
    return `${environment.ApiLink}/bono/${code}`;
  }

  openCreateModal(template: TemplateRef<any>): void {
    this.createForm.reset({ medic_id: null, assistant_id: null, label: '' });
    this.createMedicSearch = '';
    this.createMedicResults = [];
    this.createMedicLoading = false;
    this.showCreateMedicDropdown = false;
    this.createSubmitted = false;
    this.assistants = [];
    this.modalRef = this._modalService.show(template);
  }

  createQr(): void {
    this.createSubmitted = true;
    if (this.createForm.invalid) {
      return;
    }
    this.creating = true;

    const raw = this.createForm.value;
    const body: any = { medic_id: Number(raw.medic_id) };
    if (raw.assistant_id) { body.assistant_id = Number(raw.assistant_id); }
    if (raw.label) { body.label = raw.label; }

    this._bonusqr.createBonusQr(body).subscribe(
      () => {
        this.creating = false;
        this.modalRef.hide();
        this._helper.modalSuccess('QR creado exitosamente');
        this.load();
      },
      (error) => {
        this.creating = false;
        this._helper.modalError((error.error && error.error.message) || 'No se pudo crear el QR');
      }
    );
  }
}
